import { useState, useEffect } from 'react';
import { Check, ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import { T, LogoMark, Wordmark } from '../theme';
import { getPricingConfig } from '../utils/accountStorage';
import { DEFAULT_PRICING, PLAN_KEYS } from '../config/pricingDefaults';
import GlobalFooterBar from './GlobalFooterBar';

function mergePricing(remote) {
  if (!remote) return DEFAULT_PRICING;
  const merged = {};
  for (const key of PLAN_KEYS) {
    merged[key] = { ...DEFAULT_PRICING[key], ...(remote[key] || {}) };
    // Firestore stores features/missing as arrays; coerce from newline strings if needed
    for (const field of ['features', 'missing']) {
      if (typeof merged[key][field] === 'string') {
        merged[key][field] = merged[key][field].split('\n').map(s => s.trim()).filter(Boolean);
      }
    }
  }
  return merged;
}

function PlanCard({ plan, planKey, onGetStarted }) {
  const accent = planKey === 'pro' ? T.amberDeep : T.tealDeep;
  const gradBg  = planKey === 'pro' ? T.gradAmber : T.gradTeal;
  const shadowC = planKey === 'pro' ? 'rgba(217,123,40,0.3)' : 'rgba(5,196,176,0.3)';

  return (
    <div style={{
      flex: '1 1 260px',
      maxWidth: 320,
      background: '#fff',
      borderRadius: 20,
      padding: '32px 24px',
      boxShadow: plan.highlight
        ? `0 8px 40px rgba(10,124,138,0.18), 0 0 0 2px ${T.tealDeep}`
        : '0 2px 16px rgba(0,0,0,0.07)',
      position: 'relative',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {plan.badge && (
        <div style={{
          position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
          background: T.gradTeal, color: '#fff', fontSize: 11, fontWeight: 800,
          padding: '4px 16px', borderRadius: 20, letterSpacing: 0.5, whiteSpace: 'nowrap',
        }}>
          {plan.badge}
        </div>
      )}

      <div style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: 1 }}>
          {plan.name}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
        <span style={{ fontSize: 38, fontWeight: 900, color: T.navy, fontFamily: "'Courier New', monospace" }}>
          {plan.price}
        </span>
        {plan.priceNote && (
          <span style={{ fontSize: 13, color: '#9CA3AF' }}>{plan.priceNote}</span>
        )}
      </div>

      {planKey === 'basic' && (
        <div style={{
          background: T.tealPale, borderRadius: 8, padding: '6px 10px',
          fontSize: 12, fontWeight: 700, color: T.tealDeep, marginBottom: 20,
        }}>
          Always free — no credit card, ever
        </div>
      )}

      <div style={{ flex: 1, marginTop: planKey !== 'basic' ? 20 : 0, marginBottom: 24 }}>
        {plan.features.map(f => (
          <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 9 }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: planKey === 'pro' ? T.amberPale : T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              <Check size={10} color={accent} />
            </div>
            <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.45 }}>{f}</span>
          </div>
        ))}
        {plan.missing?.map(f => (
          <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 9, opacity: 0.3 }}>
            <div style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 8, height: 1.5, background: '#9CA3AF', borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.45 }}>{f}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onGetStarted}
        style={{
          width: '100%', padding: '13px', borderRadius: 12, border: 'none',
          background: (plan.highlight || planKey === 'pro') ? gradBg : T.appBg,
          color: (plan.highlight || planKey === 'pro') ? '#fff' : T.navy,
          fontSize: 14, fontWeight: 700, cursor: 'pointer',
          boxShadow: (plan.highlight || planKey === 'pro') ? `0 4px 15px ${shadowC}` : 'none',
        }}
      >
        {planKey === 'basic' ? 'Get Started Free' : `Start ${plan.name}`}
      </button>
    </div>
  );
}

function PricingPage({ onBack, onSignUp, onSignIn }) {
  const [pricing, setPricing]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    getPricingConfig().then(remote => {
      if (remote) {
        setLastUpdated(remote._updatedAt || null);
      }
      setPricing(mergePricing(remote));
      setLoading(false);
    });
  }, []);

  const plans = pricing
    ? PLAN_KEYS.map(k => ({ key: k, ...pricing[k] })).sort((a, b) => a.order - b.order)
    : [];

  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh', paddingBottom: 36 }}>

      {/* Nav */}
      <nav style={{
        background: T.navy, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={onBack}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
              padding: '6px 12px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
            }}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
          <LogoMark size={24} />
          <Wordmark size={16} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onSignIn} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>Sign In</button>
          <button onClick={onSignUp} style={{
            background: T.gradTeal, border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>Get Started Free</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ background: T.gradNavy, padding: '60px 24px 72px', textAlign: 'center' }}>
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(5,196,176,0.15)', border: '1px solid rgba(5,196,176,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 20,
          }}>
            <Zap size={13} color={T.tealBright} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.tealBright, letterSpacing: 0.5 }}>SIMPLE PRICING</span>
          </div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, color: '#fff', margin: '0 0 16px', lineHeight: 1.15 }}>
            Free to start.<br />
            <span style={{ color: T.tealBright }}>Upgrade when you need more.</span>
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', margin: '0 0 8px', lineHeight: 1.6 }}>
            MySpend.info is free forever. No trial periods, no hidden fees. The paid plans unlock higher usage limits and advanced features for power users.
          </p>
          <p style={{ fontSize: 15, fontWeight: 800, color: T.tealBright, margin: 0 }}>
            No credit card required to get started.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section style={{ padding: '64px 24px', background: T.appBg }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: 14, color: '#9CA3AF', fontWeight: 600 }}>Loading plans…</div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
              {plans.map(plan => (
                <PlanCard
                  key={plan.key}
                  plan={plan}
                  planKey={plan.key}
                  onGetStarted={onSignUp}
                />
              ))}
            </div>
          )}
          {lastUpdated && (
            <p style={{ textAlign: 'center', fontSize: 12, color: '#D1D5DB', marginTop: 32 }}>
              Pricing last updated {new Date(lastUpdated).toLocaleDateString()}
            </p>
          )}
        </div>
      </section>

      {/* FAQ strip */}
      <section style={{ background: '#fff', padding: '56px 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 36px' }}>
            Common questions
          </h2>
          {[
            ['Is the Free plan really free?', 'Yes. The Free plan has no expiry and no credit card is ever required.'],
            ['What happens if I hit my scan limit?', 'You will not be able to scan any more receipts until your next billing cycle. You can still add expenses manually. Upgrading immediately resets your allowance, or wait for the next month.'],
            ['Can I cancel at any time?', 'Yes. You can cancel your paid plan at any time. Your plan stays active until the end of the billing period, then you automatically roll back to the Free plan.'],
            // ['Is my data safe?', 'Your expense data is stored in Google Firebase, protected by Firebase\'s security rules. Receipt images sent for AI scanning are processed and not retained beyond the request. You can export or delete your data at any time.'],
          ].map(([q, a]) => (
            <div key={q} style={{ marginBottom: 28 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: T.navy, margin: '0 0 6px' }}>{q}</h3>
              <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.7, margin: 0 }}>{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: T.gradNavy, padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <h2 style={{ fontSize: 28, fontWeight: 900, color: '#fff', margin: '0 0 12px' }}>
            Ready to get started?
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', margin: '0 0 28px' }}>
            Create your free account in seconds.
          </p>
          <button onClick={onSignUp} style={{
            padding: '15px 36px', borderRadius: 14, border: 'none',
            background: T.gradTeal, color: '#fff', fontSize: 15, fontWeight: 800,
            cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            Create Free Account <ArrowRight size={17} />
          </button>
          <div style={{ marginTop: 14 }}>
            <button onClick={onSignIn} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)',
              fontSize: 13, cursor: 'pointer',
            }}>
              Already have an account? Sign in →
            </button>
          </div>
        </div>
      </section>
      <GlobalFooterBar />
    </div>
  );
}

export default PricingPage;
